<!-- 我的审核 -->
<template>
    <div class="page">
        <!-- 顶部管理员信息 -->
        <div class="header">
            <button
                type="button"
                class="header-guide-btn"
                @click="goReviewGuide"
            >
                审核指南
            </button>
            <div class="user-row">
                <div class="avatar-wrap">
                    <div class="avatar">
                        <img :src="defaultAvatar" alt="头像" />
                    </div>
                    <span class="avatar-check">
                        <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="8" cy="8" r="8" fill="#22C55E"/>
                            <path d="M4.5 8.2l2.2 2.2L11.5 5.5" stroke="#fff" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                    </span>
                </div>
                <div class="user-info">
                    <div v-if="roleLabel" class="role">{{ roleLabel }}</div>
                    <div class="phone">{{ maskedPhone }}</div>
                    <div class="tip">仅显示你所管理项目的会议室预约</div>
                </div>
            </div>
        </div>

        <!-- 状态切换 -->
        <div class="tabs-wrap">
            <button
                type="button"
                class="tab-btn"
                :class="{ active: activeTab === 'pending' }"
                @click="activeTab = 'pending'"
            >
                <span class="tab-text">待审核</span>
                <span v-if="pendingCount > 0" class="tab-badge">{{ pendingCount }}</span>
            </button>
            <button
                type="button"
                class="tab-btn"
                :class="{ active: activeTab === 'done' }"
                @click="activeTab = 'done'"
            >
                <span class="tab-text">已处理</span>
            </button>
        </div>

        <!-- 列表 / 空状态 -->
        <div class="content">
            <div v-if="filteredList.length" class="review-list">
                <div
                    v-for="item in filteredList"
                    :key="item.id"
                    class="review-card"
                >
                    <div class="card-top">
                        <span
                            class="status-badge"
                            :class="statusClass(item)"
                        >{{ statusText(item) }}</span>
                    </div>

                    <div class="card-body">
                        <div class="left-info">
                            <div class="room-name">
                                {{ item.roomName }}<template v-if="item.floor">({{ item.floor }})</template>
                            </div>
                            <div class="time">{{ item.date }} · {{ item.timeRange }}</div>
                            <div class="meta">{{ item.location }}</div>
                        </div>
                        <div class="right-info">
                            <div class="info-row">
                                <span class="label">预约客户信息:</span>
                                <span class="value">{{ item.customerPhone }}</span>
                            </div>
                            <div class="info-row info-row-wrap">
                                <span class="label">公司名称:</span>
                                <span class="value">{{ item.rentCompanyName || '—' }}</span>
                            </div>
                            <div class="info-row info-row-wrap">
                                <span class="label">公司室号:</span>
                                <span class="value">{{ item.rentCompanyHouseNo || '—' }}</span>
                            </div>
                            <div class="info-row">
                                <span class="label">申请时间:</span>
                                <span class="value">{{ item.applyTime }}</span>
                            </div>
                            <div class="info-row">
                                <span class="label">审核时间:</span>
                                <span class="value">{{ item.reviewTime || '尚未审核' }}</span>
                            </div>
                        </div>
                    </div>

                    <div v-if="item.status === 'pending'" class="card-actions">
                        <button
                            class="btn btn-outline"
                            type="button"
                            @click="handleReject(item)"
                        >不通过</button>
                        <button
                            class="btn btn-primary"
                            type="button"
                            @click="handleApprove(item)"
                        >审核通过</button>
                    </div>

                    <div
                        v-else-if="item.resultType === 'rejected' && item.reason"
                        class="reject-reason-bar"
                    >
                        拒绝原因：{{ item.reason }}
                    </div>
                </div>
            </div>

            <div v-else class="empty">
                <div class="empty-icon">
                    <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect x="8" y="8" width="32" height="34" rx="4" stroke="#A8B4C4" stroke-width="2.2"/>
                        <path d="M16 18h16M16 25h16M16 32h10" stroke="#A8B4C4" stroke-width="2.2" stroke-linecap="round"/>
                    </svg>
                </div>
                <p class="empty-title">{{ activeTab === 'pending' ? '暂无待审核' : '暂无已处理记录' }}</p>
                <p class="empty-desc">相关审核记录会显示在这里</p>
            </div>
        </div>

        <review-rejected
            v-model="showReject"
            :reservation-id="rejectTarget.id"
            :room-name="rejectTarget.roomName"
            :floor="rejectTarget.floor"
            :date="rejectTarget.date"
            :time-range="rejectTarget.timeRange"
            @confirm="confirmReject"
        />

        <approval-successful
            v-model="showApprove"
            :reservation-id="approveTarget.id"
            :room-name="approveTarget.roomName"
            :floor="approveTarget.floor"
            :date="approveTarget.date"
            :time-range="approveTarget.timeRange"
            @confirm="confirmApprove"
        />
    </div>
</template>

<script>
import reviewRejected from './reviewRejected.vue'
import approvalSuccessful from './approvalSuccessful.vue'
import scrollKeep from '@/tools/scrollKeep'
import { isUnauthorizedError, parseIsAdmin, parseIsSuperAdmin } from '@/tools/utils'
import { getReservationsRequest } from '@/request/api'
import { Toast } from 'vant'
import defaultAvatar from '@/assets/icon_avatar.png'

function pad(n) {
    return n < 10 ? `0${n}` : `${n}`
}

function parseApiDateTime(str) {
    const raw = String(str || '').trim()
    if (!raw) return null
    let d = new Date(raw)
    if (!Number.isNaN(d.getTime())) return d
    d = new Date(raw.replace(/-/g, '/'))
    return Number.isNaN(d.getTime()) ? null : d
}

function formatDisplayDate(date) {
    return `${date.getMonth() + 1}-${date.getDate()}`
}

function formatDisplayDateTime(date) {
    return `${formatDisplayDate(date)} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function formatTimeFromDate(date) {
    return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function getApiReservationStatus(item) {
    const raw = item.status != null ? item.status : item.reservationStatus
    const n = Number(raw)
    return Number.isNaN(n) ? null : n
}

function resolveReviewResult(item, apiStatus) {
    // 接口 status：1-待审核 2-已审核 3-已使用 4-已取消 5-已拒绝
    if (apiStatus === 1) {
        return { status: 'pending', resultType: 'pending' }
    }
    if (apiStatus === 2) {
        return { status: 'done', resultType: 'approved' }
    }
    if (apiStatus === 3) {
        return { status: 'done', resultType: 'used' }
    }
    if (apiStatus === 4) {
        return { status: 'done', resultType: 'cancelled' }
    }
    if (apiStatus === 5) {
        return { status: 'done', resultType: 'rejected' }
    }

    const statusLabel = String(item.statusName || item.statusLabel || '').trim()
    if (statusLabel === '待审核') {
        return { status: 'pending', resultType: 'pending' }
    }
    if (statusLabel === '已审核') {
        return { status: 'done', resultType: 'approved' }
    }
    if (statusLabel === '已使用') {
        return { status: 'done', resultType: 'used' }
    }
    if (statusLabel === '已取消') {
        return { status: 'done', resultType: 'cancelled' }
    }
    if (statusLabel === '已拒绝') {
        return { status: 'done', resultType: 'rejected' }
    }

    if (apiStatus != null) {
        return { status: 'done', resultType: 'approved' }
    }
    return { status: 'pending', resultType: 'pending' }
}

function formatReviewLocation(room, item) {
    const buildingName = String(room.buildingName || item.buildingName || '').trim()
    const floor = room.floor != null && String(room.floor).trim() !== ''
        ? String(room.floor).trim()
        : (item.floor != null && String(item.floor).trim() !== '' ? String(item.floor).trim() : '')
    if (buildingName && floor) {
        return `${buildingName}`
    }
    return buildingName || floor || '—'
}

function mapReviewItem(item) {
    if (!item || typeof item !== 'object') return null
    const id = item.id != null ? item.id : item.reservationId
    if (id == null) return null

    const apiStatus = getApiReservationStatus(item)
    const { status, resultType } = resolveReviewResult(item, apiStatus)

    const startRaw = item.startTime || item.startDateTime || item.beginTime
    const endRaw = item.endTime || item.endDateTime || item.finishTime
    const startDate = parseApiDateTime(startRaw)
    const endDate = parseApiDateTime(endRaw)

    let date = item.date || ''
    let timeRange = item.timeRange || item.time || ''
    if (startDate && endDate) {
        date = formatDisplayDate(startDate)
        timeRange = `${formatTimeFromDate(startDate)}—${formatTimeFromDate(endDate)}`
    }

    const room = item.room && typeof item.room === 'object' ? item.room : {}
    const roomName = String(room.name || item.roomName || '').trim()
    const floor = room.floor != null && String(room.floor).trim() !== ''
        ? String(room.floor).trim()
        : (item.floor != null && String(item.floor).trim() !== '' ? String(item.floor).trim() : '')
    const capacity = room.capacity != null ? room.capacity : item.capacity
    const location = formatReviewLocation(room, item)

    const customerName = String(
        item.userName || item.contactName || item.customerName || item.memberName || ''
    ).trim()
    const customerPhone = String(
        item.phone || item.contactPhone || item.customerPhone || item.userPhone || ''
    ).trim()
    const rentCompanyName = String(item.rentCompanyName || '').trim()
    const rentCompanyHouseNo = String(item.rentCompanyHouseNo || '').trim()

    const applyDate = parseApiDateTime(item.createTime)
    const reviewDate = parseApiDateTime(item.auditTime)
    const remark = String(
        item.rejectReason || item.remark || item.statusRemark || item.reason || ''
    ).trim()

    return {
        id,
        status,
        resultType,
        roomName,
        floor,
        date,
        timeRange,
        location,
        capacity,
        customerName: customerName || '—',
        customerPhone,
        rentCompanyName,
        rentCompanyHouseNo,
        applyTime: applyDate ? formatDisplayDateTime(applyDate) : '',
        reviewTime: reviewDate ? formatDisplayDateTime(reviewDate) : '',
        remark: resultType === 'cancelled' && !remark
            ? '预约开始前未完成审核，系统自动取消'
            : remark,
        reason: String(item.reason || item.rejectReason || '').trim(),
    }
}

export default {
    name: 'myReviews',
    mixins: [scrollKeep('myReviews')],
    components: {
        reviewRejected,
        approvalSuccessful,
    },
    data() {
        return {
            defaultAvatar,
            phone: '',
            isAdmin: false,
            isSuperAdmin: false,
            activeTab: 'pending',
            reviewList: [],
            listLoading: false,
            showReject: false,
            rejectTarget: {
                id: null,
                roomName: '',
                floor: '',
                date: '',
                timeRange: '',
            },
            showApprove: false,
            approveTarget: {
                id: null,
                roomName: '',
                floor: '',
                date: '',
                timeRange: '',
            },
        }
    },
    computed: {
        maskedPhone() {
            const phone = this.phone || ''
            if (/^1\d{10}$/.test(phone)) {
                return phone.replace(/^(\d{3})\d{4}(\d{4})$/, '$1****$2')
            }
            return phone || '未绑定手机号'
        },
        roleLabel() {
            if (this.isSuperAdmin) return '会议室超级管理员'
            if (this.isAdmin) return '会议室管理员'
            return ''
        },
        pendingCount() {
            return this.reviewList.filter((item) => item.status === 'pending').length
        },
        filteredList() {
            if (this.activeTab === 'pending') {
                return this.reviewList.filter((item) => item.status === 'pending')
            }
            return this.reviewList.filter((item) => item.status === 'done')
        },
    },
    watch: {
        pendingCount: {
            immediate: true,
            handler(val) {
                localStorage.setItem('pendingReviewCount', String(val))
                window.dispatchEvent(new CustomEvent('pending-review-count', { detail: val }))
            },
        },
    },
    activated() {
        this.loadUser()
        this.loadReviews()
    },
    mounted() {
        this.loadUser()
        this.loadReviews()
    },
    methods: {
        /**
         * 从本地缓存读取当前登录用户信息，用于顶部展示角色与手机号。
         * 读取失败或字段缺失时，将 phone 置为空字符串，由 computed maskedPhone 显示兜底文案。
         */
        loadUser() {
            try {
                const info = JSON.parse(localStorage.getItem('userInfo') || '{}')
                this.phone = info.phone || ''
                this.isAdmin = parseIsAdmin(info.isAdmin)
                this.isSuperAdmin = parseIsSuperAdmin(info.isSuperAdmin)
            } catch (e) {
                this.phone = ''
                this.isAdmin = false
                this.isSuperAdmin = false
            }
        },
        extractResponseList(res) {
            if (Array.isArray(res)) return res
            if (res && Array.isArray(res.data)) return res.data
            if (res && res.data && Array.isArray(res.data.list)) return res.data.list
            if (res && Array.isArray(res.list)) return res.list
            return []
        },
        /**
         * 加载会议室预约审核列表。
         * 列表项字段说明：
         * - status: 列表筛选用，`pending` 待审核（接口 status=1）/ `done` 已处理（接口 status=2~5）
         * - resultType: 审核结果展示用，`pending` | `approved` | `used` | `cancelled` | `rejected`
         * - remark: 不通过原因或系统说明（如超时自动取消）
         */
        loadReviews() {
            if (this.listLoading) return

            this.listLoading = true
            Toast.loading({
                message: '正在加载',
                forbidClick: true,
                duration: 0,
            })

            getReservationsRequest({})
                .then((res) => {
                    this.reviewList = this.extractResponseList(res)
                        .map((item) => mapReviewItem(item))
                        .filter(Boolean)
                })
                .catch((err) => {
                    if (isUnauthorizedError(err)) return
                    this.reviewList = []
                    const msg = (err && (err.message || err.msg)) || '获取审核列表失败'
                    this.$toast && this.$toast(msg)
                })
                .finally(() => {
                    this.listLoading = false
                    Toast.clear()
                })
        },
        /**
         * 根据审核结果类型返回卡片左上角状态文案。
         * @param {Object} item - 单条审核记录
         * @param {string} item.resultType - 结果类型
         * @param {string} [item.resultLabel] - 自定义文案（map 未命中时使用）
         * @returns {string} 展示用状态文字
         */
        statusText(item) {
            const map = {
                pending: '待审核',
                approved: '已审核',
                used: '已使用',
                cancelled: '已取消',
                rejected: '已拒绝',
            }
            return map[item.resultType] || item.resultLabel || ''
        },
        /**
         * 根据审核结果类型返回状态徽章的 CSS 类名对象，供 :class 绑定。
         * @param {Object} item - 单条审核记录
         * @returns {Object} 形如 { 'is-pending': boolean, ... }
         */
        statusClass(item) {
            return {
                'is-pending': item.resultType === 'pending',
                'is-approved': item.resultType === 'approved',
                'is-used': item.resultType === 'used',
                'is-cancelled': item.resultType === 'cancelled',
                'is-rejected': item.resultType === 'rejected',
            }
        },
        /**
         * 格式化为「审核时间」展示字符串：月/日 时:分（24 小时制，分钟补零）。
         * 用于管理员点击通过/不通过时写入 reviewTime。
         * @returns {string} 例如 `7/27 09:58`
         */
        nowText() {
            const d = new Date()
            const m = d.getMonth() + 1
            const day = d.getDate()
            const hh = String(d.getHours()).padStart(2, '0')
            const mm = String(d.getMinutes()).padStart(2, '0')
            return `${m}-${day} ${hh}:${mm}`
        },
        /**
         * 点击列表「不通过」：记录当前操作的预约 id 与展示信息，打开底部不通过确认弹层。
         * @param {Object} item - 待审核的预约记录
         */
        handleReject(item) {
            this.rejectTarget = {
                id: item.id,
                roomName: item.roomName,
                floor: item.floor,
                date: item.date,
                timeRange: item.timeRange,
            }
            this.showReject = true
        },
        /**
         * 不通过弹层审核成功后的回调，刷新列表数据。
         */
        confirmReject() {
            this.loadReviews()
        },
        /**
         * 点击列表「审核通过」：记录当前操作的预约 id 与展示信息，打开底部通过确认弹层。
         * @param {Object} item - 待审核的预约记录
         */
        handleApprove(item) {
            this.approveTarget = {
                id: item.id,
                roomName: item.roomName,
                floor: item.floor,
                date: item.date,
                timeRange: item.timeRange,
            }
            this.showApprove = true
        },
        /**
         * 通过弹层审核成功后的回调，刷新列表数据。
         */
        confirmApprove() {
            this.loadReviews()
        },
        goReviewGuide() {
            this.$router.push('/myReviews/reviewGuide')
        },
    },
}
</script>

<style scoped>
.page {
    min-height: calc(100vh - 1.2rem - env(safe-area-inset-bottom));
    background: #f2f4f8;
    /* 列表底部少量间距；tabBar 高度由 MainTab placeholder 预留 */
    padding-bottom: 0.2rem;
    box-sizing: border-box;
}

.header {
    position: relative;
    background: linear-gradient(160deg, #163a68 0%, #0d2c54 100%);
    padding: 0.4rem 0.36rem 0.56rem;
    box-sizing: border-box;
}

.header-guide-btn {
    position: absolute;
    z-index: 1;
    top: calc(0.3rem + env(safe-area-inset-top, 0px));
    right: 0rem;
    padding: 0.1rem 0.22rem;
    border-radius: 0.32rem 0 0 0.32rem;
    background: #2b7de9;
    color: #ffffff;
    font-size: 0.24rem;
    line-height: 1.4;
    cursor: pointer;
    backdrop-filter: blur(4px);
}

.user-row {
    display: flex;
    align-items: flex-start;
}

.avatar-wrap {
    position: relative;
    width: 1rem;
    height: 1rem;
    flex-shrink: 0;
}

.avatar {
    width: 100%;
    height: 100%;
    border-radius: 50%;
    overflow: hidden;
    background: #f5e6d3;
}

.avatar img {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
}

.avatar-check {
    position: absolute;
    right: -0.02rem;
    bottom: -0.02rem;
    width: 0.32rem;
    height: 0.32rem;
}

.avatar-check svg {
    width: 100%;
    height: 100%;
    display: block;
}

.user-info {
    margin-left: 0.22rem;
    min-width: 0;
    padding-top: 0.02rem;
}

.role {
    font-size: 0.24rem;
    color: rgba(255, 255, 255, 0.8);
    line-height: 1.3;
}

.phone {
    margin-top: 0.06rem;
    font-size: 0.36rem;
    font-weight: 700;
    color: #ffffff;
    letter-spacing: 0.01rem;
    line-height: 1.2;
}

.tip {
    margin-top: 0.1rem;
    font-size: 0.22rem;
    color: rgba(255, 255, 255, 0.65);
    line-height: 1.3;
}

.tabs-wrap {
    display: flex;
    align-items: center;
    gap: 0.36rem;
    margin: 0.28rem 0.32rem 0;
    position: relative;
    z-index: 2;
}

.tab-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 0.92rem;
    border: none;
    border-radius: 0.46rem;
    background: rgba(255, 255, 255, 0.55);
    color: #aab6c5;
    font-size: 0.3rem;
    font-weight: 400;
    box-shadow: none;
    cursor: pointer;
    transition: all 0.2s ease;
}

.tab-btn.active {
    background: #ffffff;
    color: #1a3558;
    font-weight: 600;
    box-shadow:
        0 0.02rem 0.06rem rgba(30, 58, 95, 0.04),
        0 0.1rem 0.28rem rgba(30, 58, 95, 0.1);
}

.tab-text {
    line-height: 1;
}

.tab-badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0.34rem;
    height: 0.34rem;
    margin-left: 0.1rem;
    padding: 0 0.06rem;
    border-radius: 50%;
    background: #ef4444;
    color: #ffffff;
    font-size: 0.2rem;
    font-weight: 500;
    line-height: 1;
    box-sizing: border-box;
}

.content {
    padding-top: 0.28rem;
}

.review-list {
    padding: 0 0.28rem 0.32rem;
}

.review-card {
    background: #ffffff;
    border-radius: 0.24rem;
    padding: 0.28rem;
    margin-bottom: 0.2rem;
    box-shadow: 0 0.06rem 0.2rem rgba(40, 90, 160, 0.05);
    box-sizing: border-box;
}

.card-top {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.2rem;
}

.status-badge {
    display: inline-block;
    padding: 0.02rem 0.14rem;
    border-radius: 0.2rem;
    font-size: 0.2rem;
    line-height: 1.5;
}

.status-badge.is-pending {
    background: #fff4e5;
    color: #d4923a;
}

.status-badge.is-rejected {
    background: #fde8e8;
    color: #e2554a;
}

.status-badge.is-approved {
    background: #e6f7ee;
    color: #2f9b63;
}

.status-badge.is-used {
    background: #e8f2fd;
    color: #2b7de9;
}

.status-badge.is-cancelled {
    background: #eef1f5;
    color: #8a9bb0;
}

.card-body {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 0.2rem;
}

.left-info {
    flex: 1;
    min-width: 0;
}

.room-name {
    font-size: 0.34rem;
    font-weight: 700;
    color: #1e3a5f;
    line-height: 1.3;
}

.time {
    margin-top: 0.1rem;
    font-size: 0.26rem;
    font-weight: 600;
    color: #2b7de9;
    line-height: 1.35;
}

.meta {
    margin-top: 0.08rem;
    font-size: 0.22rem;
    color: #8a9bb0;
    line-height: 1.4;
}

.right-info {
    flex-shrink: 0;
    max-width: 3.4rem;
    text-align: left;
}

.info-row {
    font-size: 0.2rem;
    line-height: 1.55;
    color: #8a9bb0;
}

.info-row + .info-row {
    margin-top: 0.04rem;
}

.info-row .label {
    color: #9aa8bc;
}

.info-row .value {
    color: #64748b;
    margin-left: 0.04rem;
    word-break: break-all;
}

.info-row-wrap .value {
    white-space: pre-wrap;
    word-break: break-word;
}

.card-actions {
    display: flex;
    gap: 0.2rem;
    margin-top: 0.28rem;
}

.btn {
    flex: 1;
    height: 0.8rem;
    border-radius: 0.4rem;
    font-size: 0.3rem;
    cursor: pointer;
}

.btn-outline {
    border: 1px solid #d5dee9;
    background: #ffffff;
    color: #64748b;
}

.btn-primary {
    border: none;
    background: #2b7de9;
    color: #ffffff;
    font-weight: 500;
}

.btn-primary:active {
    background: #246fce;
}

.reject-reason-bar {
    margin-top: 0.24rem;
    padding: 0.16rem 0.2rem;
    border-radius: 0.12rem;
    background: #fff5f5;
    color: #c0392b;
    font-size: 0.22rem;
    line-height: 1.45;
    word-break: break-all;
}

.empty {
    padding: 1.6rem 0.4rem 0;
    text-align: center;
}

.empty-icon {
    width: 0.72rem;
    height: 0.72rem;
    margin: 0 auto 0.28rem;
}

.empty-icon svg {
    width: 100%;
    height: 100%;
    display: block;
}

.empty-title {
    margin: 0;
    font-size: 0.32rem;
    font-weight: 600;
    color: #1e3a5f;
}

.empty-desc {
    margin: 0.16rem 0 0;
    font-size: 0.24rem;
    color: #9aa8bc;
}
</style>
